import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';

interface ChatStatus {
  isBanned: boolean;
  banReason: string | null;
  banExpiresAt: string | null;
}

// Default status when API fails - assume user is not banned
const DEFAULT_CHAT_STATUS: ChatStatus = {
  isBanned: false,
  banReason: null,
  banExpiresAt: null,
};

// Request timeout in milliseconds - prevent hanging requests
const REQUEST_TIMEOUT = 10000; // 10 seconds

/**
 * Helper to add timeout to a promise
 */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('Chat status request timed out')), ms)
    ),
  ]);
}

/**
 * Hook to check if the current user is banned from chat platform-wide
 */
export function useChatStatus() {
  const query = useQuery<ChatStatus>({
    queryKey: ['chat-status'],
    queryFn: async () => {
      try {
        // Add timeout to prevent hanging on slow/stuck requests
        const response = await withTimeout(
          apiRequest('/api/user/chat-status', 'GET'),
          REQUEST_TIMEOUT
        );
        return response;
      } catch (error: any) {
        console.warn('[useChatStatus] Failed to fetch chat status:', error?.message || error);
        // Return default (not banned) on any error including timeout
        return DEFAULT_CHAT_STATUS;
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1, // Only retry once to avoid long waits
    retryDelay: 1000, // Simple 1 second delay
  });

  // If there's an error, return default status (not banned) so chat can still load
  // This prevents the chat from being blocked by a failing status endpoint
  if (query.isError) {
    return {
      ...query,
      data: DEFAULT_CHAT_STATUS,
      isLoading: false, // Don't block on error
    };
  }

  return query;
}
